import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useApi } from "@/lib/api";
import { PendingOwnershipTile } from "@/pages/Profile";
import NotMe from "@/components/NotMe";

const EDMONTON = "America/Edmonton";
// Format an ISO date (either YYYY-MM-DD or a full datetime) as a calendar
// date in Edmonton. Plain date-only strings are rendered verbatim so UTC
// midnight does not slide them to the previous day.
const fmtDate = (iso) => {
    if (!iso) return "";
    try {
        const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso));
        if (m) {
            const [, y, mo, d] = m;
            const dt = new Date(Date.UTC(+y, +mo - 1, +d, 12, 0, 0));
            return dt.toLocaleDateString("en-US", {
                timeZone: "UTC",
                month: "short",
                day: "numeric",
                year: "numeric",
            });
        }
        return new Date(iso).toLocaleDateString("en-US", {
            timeZone: EDMONTON,
            month: "short",
            day: "numeric",
            year: "numeric",
        });
    } catch {
        return "";
    }
};
const fmtDateUpper = (iso) => (fmtDate(iso) || "").toUpperCase();

// ---------- Brand mark ----------
function SamoonyMark() {
    return (
        <Link
            to="/"
            data-testid="dashboard-brand"
            className="flex items-center no-underline"
            style={{ gap: 8 }}
        >
            <svg
                width="26"
                height="26"
                viewBox="0 0 26 26"
                aria-hidden="true"
            >
                <circle cx="13" cy="6" r="3" fill="#FF2E7E" />
                <path
                    d="M5 12 a8 8 0 0 0 16 0"
                    fill="none"
                    stroke="#111110"
                    strokeWidth="3.2"
                    strokeLinecap="round"
                />
            </svg>
            <span
                className="font-display font-extrabold text-ink"
                style={{
                    fontSize: 21,
                    letterSpacing: "-0.5px",
                    lineHeight: 1,
                }}
            >
                samoony
            </span>
        </Link>
    );
}

// ---------- Setup ring ----------
function SetupRing({ done, total = 4 }) {
    const size = 220;
    const cx = size / 2;
    const cy = size / 2;
    const r = 96;
    const gap = 6; // ~10px of arc at r=96
    const seg = 360 / total - gap;
    const toXY = (deg) => {
        const rad = ((deg - 90) * Math.PI) / 180;
        return [cx + r * Math.cos(rad), cy + r * Math.sin(rad)];
    };
    const arcs = Array.from({ length: total }).map((_, i) => {
        const start = i * (seg + gap) + gap / 2;
        const end = start + seg;
        const [x1, y1] = toXY(start);
        const [x2, y2] = toXY(end);
        const large = seg > 180 ? 1 : 0;
        const isDone = i < done;
        return (
            <path
                key={i}
                d={`M ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2}`}
                stroke={isDone ? "var(--color-signal)" : "var(--color-electric)"}
                strokeWidth="14"
                strokeLinecap="round"
                fill="none"
            />
        );
    });
    return (
        <div
            data-testid="dashboard-setup-ring"
            className="relative mx-auto"
            style={{ width: size, height: size }}
        >
            <svg viewBox={`0 0 ${size} ${size}`} className="w-full h-full" aria-hidden>
                {arcs}
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
                <div className="font-display font-black text-paper leading-none text-[56px]">
                    <span data-testid="dashboard-setup-count">{done}</span>
                    <span className="text-on-ink-muted">/{total}</span>
                </div>
                <div className="mt-2 font-mono font-medium text-[11px] tracking-[0.14em] text-on-ink-muted">
                    STEPS DONE
                </div>
            </div>
        </div>
    );
}

// ---------- Named gauge ----------
function NamedGauge({ rate, band }) {
    const size = 240;
    const cx = size / 2;
    const cy = size / 2;
    const r = 102;
    const startAngle = 135;
    const endAngle = startAngle + 270;
    const toXY = (deg) => {
        const rad = ((deg - 90) * Math.PI) / 180;
        return [cx + r * Math.cos(rad), cy + r * Math.sin(rad)];
    };
    const [sx, sy] = toXY(startAngle);
    const [ex, ey] = toXY(endAngle);
    const sweep = 270 * Math.max(0, Math.min(1, Number(rate) || 0));
    const [fx, fy] = toXY(startAngle + sweep);
    const fillLarge = sweep > 180 ? 1 : 0;
    const fillColor =
        band === "strong"
            ? "var(--color-dial-strong)"
            : band === "growing"
            ? "var(--color-dial-mid)"
            : "var(--color-signal)";
    const pct = Math.round((Number(rate) || 0) * 100);
    return (
        <div
            data-testid="dashboard-named-gauge"
            className="relative mx-auto"
            style={{ width: size, height: size }}
        >
            <svg viewBox={`0 0 ${size} ${size}`} className="w-full h-full" aria-hidden>
                <path
                    d={`M ${sx} ${sy} A ${r} ${r} 0 1 1 ${ex} ${ey}`}
                    stroke="var(--color-line-dark)"
                    strokeWidth="16"
                    strokeLinecap="round"
                    fill="none"
                />
                {sweep > 0 && (
                    <path
                        d={`M ${sx} ${sy} A ${r} ${r} 0 ${fillLarge} 1 ${fx} ${fy}`}
                        stroke={fillColor}
                        strokeWidth="16"
                        strokeLinecap="round"
                        fill="none"
                    />
                )}
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
                <div
                    data-testid="dashboard-named-pct"
                    className="font-display font-black leading-none text-[64px] sm:text-[76px]"
                    style={{ color: fillColor }}
                >
                    {pct}%
                </div>
                <div className="mt-1 font-mono font-medium text-[11px] tracking-[0.14em] text-on-ink-muted">
                    NAMED
                </div>
            </div>
        </div>
    );
}

// ---------- Chip ----------
function Chip({ children, kind = "named", outlined = false, testid }) {
    const palettes = {
        named: ["var(--color-named-bg)", "var(--color-named-fg)"],
        growing: ["var(--color-growing-bg)", "var(--color-growing-fg)"],
        not_yet: ["var(--color-line)", "var(--color-muted)"],
        electric: ["var(--color-electric)", "#FFFFFF"],
        signal: ["var(--color-signal)", "var(--color-ink)"],
        "dial-mid": ["var(--color-dial-mid)", "var(--color-ink)"],
        "dial-strong": ["var(--color-dial-strong)", "var(--color-ink)"],
    };
    const [bg, fg] = palettes[kind] || palettes.named;
    const style = outlined
        ? {
              background: "transparent",
              color: "var(--color-electric)",
              border: "1px solid var(--color-electric)",
          }
        : { background: bg, color: fg };
    return (
        <span
            data-testid={testid}
            className="font-mono font-medium text-[11px] tracking-[0.14em] uppercase rounded-full px-3 py-1 whitespace-nowrap"
            style={style}
        >
            {children}
        </span>
    );
}

// ---------- Health bar ----------
function HealthBar({ label, value, fillColor, statusText, statusColor, testid }) {
    const pct = Math.max(0, Math.min(100, Number(value) || 0));
    return (
        <div data-testid={testid} className="flex flex-col gap-1.5">
            <div className="flex items-baseline justify-between gap-3">
                <span className="font-display text-paper text-[14px] font-semibold">
                    {label}
                </span>
                <span
                    data-testid={`${testid}-status`}
                    className="font-mono font-medium text-[11px] tracking-[0.14em] uppercase"
                    style={{ color: statusColor }}
                >
                    {statusText}
                </span>
            </div>
            <div
                className="w-full rounded-full overflow-hidden"
                style={{ height: 10, background: "var(--color-line-dark)" }}
                aria-hidden
            >
                <div
                    className="h-full rounded-full"
                    style={{ width: `${pct}%`, background: fillColor }}
                />
            </div>
        </div>
    );
}

// ---------- Health row (icon + label + status + value + bar) ----------
function HealthIconNamed() {
    return (
        <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true">
            <circle cx="10" cy="10" r="7" fill="none" stroke="#111110" strokeWidth="2.2" />
            <circle cx="10" cy="10" r="2.6" fill="#111110" />
        </svg>
    );
}
function HealthIconPresence() {
    return (
        <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true">
            <line x1="10" y1="10" x2="4" y2="5" stroke="#FFFFFF" strokeWidth="1.8" />
            <line x1="10" y1="10" x2="16" y2="5" stroke="#FFFFFF" strokeWidth="1.8" />
            <line x1="10" y1="10" x2="10" y2="17" stroke="#FFFFFF" strokeWidth="1.8" />
            <circle cx="10" cy="10" r="3" fill="#FFFFFF" />
            <circle cx="4" cy="5" r="2" fill="#FFFFFF" />
            <circle cx="16" cy="5" r="2" fill="#FFFFFF" />
            <circle cx="10" cy="17" r="2" fill="#FFFFFF" />
        </svg>
    );
}
function HealthIconFreshness() {
    return (
        <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true">
            <path
                d="M11.5 2 L5 11 H9.5 L8.5 18 L15 9 H10.5 Z"
                fill="#111110"
            />
        </svg>
    );
}

function HealthRow({
    testid,
    label,
    statusText,
    statusColor,
    valueText,
    valueColor,
    valueFontFamily,
    valueFontSize,
    valueFontWeight,
    valueLetterSpacing,
    iconBg,
    iconBoxShadow,
    icon,
    barValue,
    barFill,
}) {
    const pct = Math.max(0, Math.min(100, Number(barValue) || 0));
    return (
        <div data-testid={testid} className="flex flex-col gap-2">
            <div className="flex items-center gap-3">
                <div
                    aria-hidden
                    className="flex items-center justify-center shrink-0"
                    style={{
                        width: 38,
                        height: 38,
                        borderRadius: 12,
                        background: iconBg,
                        boxShadow: iconBoxShadow || "none",
                    }}
                >
                    {icon}
                </div>
                <div className="flex-1 min-w-0 flex flex-col">
                    <span className="font-display text-paper text-[16px] font-bold leading-tight">
                        {label}
                    </span>
                    <span
                        data-testid={`${testid}-status`}
                        className="font-mono font-medium uppercase"
                        style={{
                            color: statusColor,
                            fontSize: 11,
                            letterSpacing: "1px",
                            marginTop: 2,
                        }}
                    >
                        {statusText}
                    </span>
                </div>
                <span
                    data-testid={`${testid}-value`}
                    style={{
                        color: valueColor,
                        fontFamily: valueFontFamily || "Archivo, system-ui, sans-serif",
                        fontWeight: valueFontWeight ?? 900,
                        fontSize: valueFontSize ?? 26,
                        letterSpacing: valueLetterSpacing || "normal",
                        lineHeight: 1,
                    }}
                >
                    {valueText}
                </span>
            </div>
            <div
                className="w-full rounded-full overflow-hidden"
                style={{ height: 10, background: "var(--color-line-dark)" }}
                aria-hidden
            >
                <div
                    className="h-full rounded-full"
                    style={{ width: `${pct}%`, background: barFill }}
                />
            </div>
        </div>
    );
}

// ---------- Tile shell (flat ink) ----------
function Tile({ eyebrow, chip, children, bottomLeft, bottomRight, testid }) {
    return (
        <section
            data-testid={testid}
            className="bg-ink rounded-[28px] p-5 sm:p-6 flex flex-col gap-4"
        >
            <div className="flex items-center justify-between gap-3">
                <div className="font-mono font-medium text-[11px] tracking-[0.14em] uppercase text-on-ink-muted">
                    {eyebrow}
                </div>
                {chip ? <div>{chip}</div> : null}
            </div>
            <div className="flex flex-col gap-4">{children}</div>
            {(bottomLeft || bottomRight) && (
                <div
                    className="pt-4 flex items-end justify-between gap-3"
                    style={{ borderTop: "1px solid var(--color-line-dark)" }}
                >
                    <div className="min-w-0 flex-1">{bottomLeft}</div>
                    <div className="text-right">{bottomRight}</div>
                </div>
            )}
        </section>
    );
}

// ---------- Placement network ----------
const _PLACEMENT_PROGRESS = {
    draft_pending: 0.15,
    needs_redo: 0.15,
    draft_ready: 0.35,
    published_not_verified: 0.60,
    live_not_indexed: 0.85,
    live_verified: 1.0,
};
// One spoke per published placement, spread evenly around "you" and
// starting at the top. The map grows as placements go out.
function _placementSpokes(n) {
    const out = [];
    for (let i = 0; i < n; i++) {
        const a = -Math.PI / 2 + (2 * Math.PI * i) / n;
        out.push({ x: 157 + 112 * Math.cos(a), y: 125 + 97 * Math.sin(a) });
    }
    return out;
}

// Category icon drawn centred inside a placed node, replacing the previous
// r5 centre dot. All coordinates are relative to (0, 0); the parent <g>
// positions it at the node centre. Stroke 1.6, round caps and joins.
function CategoryIcon({ category, color }) {
    const common = {
        fill: "none",
        stroke: color,
        strokeWidth: 1.6,
        strokeLinecap: "round",
        strokeLinejoin: "round",
    };
    const key = String(category || "").trim();
    if (key === "Article") {
        return (
            <g aria-hidden="true">
                <path d="M-4 -6 H4 V6 H-4 Z" {...common} />
                <path d="M-2 -2 H2" {...common} />
                <path d="M-2 1 H2" {...common} />
                <path d="M-2 4 H1" {...common} />
            </g>
        );
    }
    if (key === "Newsletter") {
        return (
            <g aria-hidden="true">
                <path d="M-6 -4 H6 V4 H-6 Z" {...common} />
                <path d="M-6 -4 L0 1 L6 -4" {...common} />
            </g>
        );
    }
    if (key === "Network") {
        return (
            <g aria-hidden="true">
                <path d="M0 -4 L-4 3 M0 -4 L4 3 M-4 3 L4 3" {...common} />
                <circle cx={0} cy={-4} r={1.8} fill={color} />
                <circle cx={-4} cy={3} r={1.8} fill={color} />
                <circle cx={4} cy={3} r={1.8} fill={color} />
            </g>
        );
    }
    if (key === "Directory") {
        return (
            <g aria-hidden="true">
                <circle cx={-5} cy={-4} r={1} fill={color} />
                <circle cx={-5} cy={0} r={1} fill={color} />
                <circle cx={-5} cy={4} r={1} fill={color} />
                <path d="M-2 -4 H5" {...common} />
                <path d="M-2 0 H5" {...common} />
                <path d="M-2 4 H5" {...common} />
            </g>
        );
    }
    // Default: "Web source" — globe
    return (
        <g aria-hidden="true">
            <circle cx={0} cy={0} r={6} {...common} />
            <ellipse cx={0} cy={0} rx={2.5} ry={6} {...common} />
            <path d="M-6 0 H6" {...common} />
        </g>
    );
}

function PlacementNetwork({ network, items, testid }) {
    const list = Array.isArray(items) ? items : [];
    const spokes = _placementSpokes(list.length);
    const n = list.length;
    const litCount = list.filter((p) => p.status === "live_verified").length;
    let headline;
    if (litCount > 0) {
        headline =
            litCount === 1
                ? "1 place on the web is talking about you."
                : `${litCount} places on the web are talking about you.`;
    } else if (n > 0) {
        headline =
            n === 1
                ? "1 place on the web is starting to mention you."
                : `${n} places on the web are starting to mention you.`;
    } else {
        headline = "We're getting you onto the web.";
    }
    const sub = n > 0
        ? "Each line fills as a mention goes live. It turns pink once people can find it."
        : "Your first ones are on the way.";
    const ariaLabel = `${n} place${n === 1 ? "" : "s"} on the web, ${litCount} found`;
    const cx = 157;
    const cy = 125;
    return (
        <div
            className="rounded-[22px] p-5 flex flex-col gap-3"
            style={{ background: "var(--color-ink-raised)" }}
            data-testid={testid}
        >
            <div className="min-w-0">
                <div
                    className="font-display text-paper text-[20px] font-black leading-tight"
                    data-testid={`${testid}-headline`}
                >
                    {headline}
                </div>
                <div className="text-on-ink-muted text-[13px] mt-1">
                    {sub}
                </div>
            </div>
            <div className="flex items-center justify-center">
                <svg
                    data-testid={`${testid}-map`}
                    aria-label={ariaLabel}
                    role="img"
                    viewBox="0 0 314 250"
                    width="100%"
                    height="auto"
                    style={{ maxWidth: 314 }}
                >
                    {/* 1. Base lines first (behind nodes). */}
                    {spokes.map((sp, i) => {
                        return (
                            <line
                                key={`base${i}`}
                                x1={cx}
                                y1={cy}
                                x2={sp.x}
                                y2={sp.y}
                                stroke="#33312D"
                                strokeWidth={4}
                                strokeLinecap="round"
                            />
                        );
                    })}
                    {/* 2. Progress overlays. */}
                    {spokes.map((sp, i) => {
                        const p = list[i];
                        if (p.status === "live_verified") {
                            return (
                                <line
                                    key={`prog${i}`}
                                    x1={cx}
                                    y1={cy}
                                    x2={sp.x}
                                    y2={sp.y}
                                    stroke="#FF2E7E"
                                    strokeWidth={4}
                                    strokeLinecap="round"
                                />
                            );
                        }
                        const frac = _PLACEMENT_PROGRESS[p.status] ?? 0.15;
                        const px = cx + (sp.x - cx) * frac;
                        const py = cy + (sp.y - cy) * frac;
                        return (
                            <g key={`prog${i}`}>
                                <line
                                    x1={cx}
                                    y1={cy}
                                    x2={px}
                                    y2={py}
                                    stroke="#2F6BFF"
                                    strokeWidth={4}
                                    strokeLinecap="round"
                                />
                                <circle cx={px} cy={py} r={4.5} fill="#8FB0FF" />
                            </g>
                        );
                    })}
                    {/* 3. Centre node. */}
                    <circle cx={cx} cy={cy} r={56} fill="#FF2E7E" opacity={0.18} />
                    <circle cx={cx} cy={cy} r={34} fill="#FF2E7E" />
                    <text
                        x={cx}
                        y={cy + 5}
                        textAnchor="middle"
                        style={{
                            fill: "var(--color-ink)",
                            fontFamily: "Archivo, system-ui, sans-serif",
                            fontWeight: 900,
                            fontSize: 14,
                            letterSpacing: 0.5,
                        }}
                    >
                        you
                    </text>
                    {/* 4. Spoke nodes with centred category icons. */}
                    {spokes.map((sp, i) => {
                        const p = list[i];
                        if (p.status === "live_verified") {
                            return (
                                <g key={`node${i}`}>
                                    <circle cx={sp.x} cy={sp.y} r={24} fill="#FF2E7E" opacity={0.22} />
                                    <circle cx={sp.x} cy={sp.y} r={14} fill="#FF2E7E" />
                                    <g transform={`translate(${sp.x} ${sp.y})`}>
                                        <CategoryIcon category={p.category} color="#111110" />
                                    </g>
                                </g>
                            );
                        }
                        return (
                            <g key={`node${i}`}>
                                <circle
                                    cx={sp.x}
                                    cy={sp.y}
                                    r={15}
                                    fill="#1F1E1B"
                                    stroke="#2F6BFF"
                                    strokeWidth={2.5}
                                />
                                <g transform={`translate(${sp.x} ${sp.y})`}>
                                    <CategoryIcon category={p.category} color="#8FB0FF" />
                                </g>
                            </g>
                        );
                    })}
                </svg>
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-1.5 justify-center font-mono font-medium text-[11px] tracking-[0.14em] uppercase text-on-ink-muted">
                <span className="inline-flex items-center gap-1.5">
                    <span
                        className="inline-block"
                        style={{
                            width: 10,
                            height: 10,
                            borderRadius: 999,
                            background: "#FF2E7E",
                        }}
                        aria-hidden
                    />
                    Found
                </span>
                <span className="inline-flex items-center gap-1.5">
                    <span
                        className="inline-block"
                        style={{
                            width: 10,
                            height: 10,
                            borderRadius: 999,
                            background: "transparent",
                            border: "2px solid #2F6BFF",
                        }}
                        aria-hidden
                    />
                    On the way
                </span>
            </div>
        </div>
    );
}

// ---------- Dashboard ----------
// `viewAs` is set by the admin View as creator screen; `?view_as=` covers
// links back from topic pages.
export default function Dashboard({ viewAs = "" }) {
    const { request } = useApi();
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const viewAsId = viewAs || params.get("view_as") || "";
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showCheckIn, setShowCheckIn] = useState(false);

    const load = useCallback(async () => {
        try {
            const url = viewAsId
                ? `/admin/dashboard/${encodeURIComponent(viewAsId)}`
                : "/dashboard";
            const res = await request(url);
            setData(res);
        } catch (err) {
            if (!viewAsId && err?.status === 404) {
                navigate("/onboarding", { replace: true });
                return;
            }
            toast.error(err?.message || "Could not load dashboard.");
        } finally {
            setLoading(false);
        }
    }, [request, navigate, viewAsId]);

    useEffect(() => {
        load();
    }, [load]);

    // The first tracked search runs right after the page goes live. Poll
    // until it lands so "Starting point recorded" flips to a check.
    const recording = !!data?.creator?.is_approved && !data?.named?.has_run;
    useEffect(() => {
        if (!recording) return undefined;
        const t = setInterval(load, 15000);
        return () => clearInterval(t);
    }, [recording, load]);

    useEffect(() => {
        if (data?.pending_check_in?.run_id && !viewAsId) {
            setShowCheckIn(true);
        }
    }, [data, viewAsId]);

    const isAdminView = !!data?.creator?.is_admin_view;

    if (loading) {
        return (
            <div
                className="min-h-screen bg-paper flex items-center justify-center text-sm"
                style={{ color: "var(--color-muted)" }}
            >
                Loading your dashboard...
            </div>
        );
    }
    if (!data) return null;

    const {
        creator,
        named,
        placements,
        page,
        next_focus_category,
        brand_health,
        building_progress,
        categories,
    } = data;
    const topics = categories || [];
    const topicHref = (slug) =>
        viewAsId
            ? `/dashboard/topic/${encodeURIComponent(slug)}?view_as=${encodeURIComponent(viewAsId)}`
            : `/dashboard/topic/${encodeURIComponent(slug)}`;
    const firstName = (creator.first_name || "there").toLowerCase();
    const isPendingStaff =
        creator.ownership_status === "pending_staff" && !creator.approved_at;

    // Pending-ownership creators see the simple waiting tile here too, with no
    // dashboard shell, no setup checklist, no build CTA. Admin views (admins
    // visiting the dashboard with ?view_as=) keep the full dashboard.
    if (isPendingStaff && !isAdminView) {
        return (
            <div className="min-h-screen bg-paper">
                <div className="mx-auto w-full max-w-[480px] px-5 pt-5 pb-3 flex items-center justify-between">
                    <SamoonyMark />
                    <Link
                        to="/account"
                        data-testid="dashboard-account-link"
                        aria-label="Account"
                        className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-ink text-paper font-display font-bold text-base"
                    >
                        {(creator.first_name || "A").slice(0, 1).toUpperCase()}
                    </Link>
                </div>
                <main className="mx-auto w-full max-w-[480px] px-5 pb-16 flex flex-col gap-5">
                    <PendingOwnershipTile testid="dashboard-pending-ownership" />
                </main>
            </div>
        );
    }

    const buildingMode = named.building_mode;
    const rateBand = named.rate_band || "getting_started";
    const bandLabel =
        rateBand === "strong"
            ? "Strong"
            : rateBand === "growing"
            ? "Growing"
            : "Getting started";

    const checklist = [
        { label: "Your page is live", done: !!creator.is_approved },
        {
            label: "Your searches are set up",
            done: !!(building_progress?.[1]?.done),
        },
        { label: "Starting point recorded", done: !!named.has_run, running: recording },
        {
            label: "Placements going live",
            done: (placements.live + placements.in_progress) > 0,
        },
    ];
    const doneCount = checklist.filter((c) => c.done).length;
    const nextIdx = checklist.findIndex((c) => !c.done);

    const newlyNamed = (named.newly_named_categories || [])[0];

    return (
        <div className="min-h-screen bg-paper">
            <div className="mx-auto w-full max-w-[480px] px-5 pt-5 pb-3 flex items-center justify-between">
                <SamoonyMark />
                <div className="flex items-center gap-2">
                    {data?.is_admin && !isAdminView && (
                        <Link
                            to="/admin/creators"
                            data-testid="dashboard-admin-link"
                            className="font-mono text-[10px] tracking-[0.14em] uppercase bg-ink text-paper rounded-full px-3 py-1"
                        >
                            Admin
                        </Link>
                    )}
                    {!isAdminView ? (
                        <Link
                            to="/account"
                            data-testid="dashboard-account-link"
                            aria-label="Account and your page"
                            className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-ink text-paper font-display font-bold text-base"
                        >
                            {(creator.first_name || "A").slice(0, 1).toUpperCase()}
                        </Link>
                    ) : (
                        <div
                            data-testid="dashboard-admin-banner"
                            className="font-mono text-[10px] tracking-[0.14em] uppercase bg-signal-tint text-signal-text rounded-full px-3 py-1"
                        >
                            Admin view
                        </div>
                    )}
                </div>
            </div>

            <main className="mx-auto w-full max-w-[480px] px-5 pb-16 flex flex-col gap-5">
                <section data-testid="dashboard-greeting" className="pt-2">
                    <div
                        className="font-mono font-medium text-[11px] tracking-[0.14em] uppercase"
                        style={{ color: "var(--color-muted)" }}
                    >
                        HI {firstName.toUpperCase()}
                    </div>
                    <h1 className="font-display font-black tracking-tight text-ink text-[40px] leading-[1.05] mt-2">
                        {buildingMode ? "We're getting you " : "You're getting "}
                        <span className="text-signal">named.</span>
                    </h1>
                </section>

                {/* ----- Brand health, first on the page ----- */}
                <Tile
                    testid="dashboard-tile-brand-health"
                    eyebrow="BRAND HEALTH"
                    chip={
                        buildingMode ? (
                            <Chip kind="electric" testid="dashboard-brand-health-chip">
                                Building
                            </Chip>
                        ) : (
                            <Chip
                                kind={
                                    (brand_health?.band === "strong"
                                        ? "dial-strong"
                                        : brand_health?.band === "growing"
                                        ? "dial-mid"
                                        : "signal")
                                }
                                testid="dashboard-brand-health-chip"
                            >
                                {brand_health?.band === "strong"
                                    ? "Strong"
                                    : brand_health?.band === "growing"
                                    ? "Growing"
                                    : "Getting started"}
                            </Chip>
                        )
                    }
                >
                    <div className="flex items-end gap-3 flex-wrap">
                        <div
                            data-testid="dashboard-health-score"
                            className="font-display font-black text-paper leading-none text-[80px] sm:text-[92px]"
                        >
                            {brand_health?.score ?? 0}
                        </div>
                        <div
                            className="font-mono font-medium pb-3 text-[16px] tracking-[0.08em]"
                            style={{ color: "var(--color-on-ink-muted)" }}
                        >
                            /100
                        </div>
                    </div>
                    <div className="flex flex-col" style={{ gap: 18 }}>
                        <HealthRow
                            testid="dashboard-health-bar-named"
                            label="Named"
                            iconBg="#FF2E7E"
                            icon={<HealthIconNamed />}
                            statusText={
                                buildingMode
                                    ? "Pending"
                                    : (brand_health?.bars?.named?.status?.label || "Not yet")
                            }
                            statusColor="#D8D5CC"
                            valueText={
                                buildingMode
                                    ? "Soon"
                                    : String(brand_health?.sub_scores?.named ?? 0)
                            }
                            valueColor={buildingMode ? "#B9B6AD" : "#FF2E7E"}
                            valueFontFamily={buildingMode ? "'Geist Mono', ui-monospace, monospace" : undefined}
                            valueFontSize={buildingMode ? 14 : 26}
                            valueFontWeight={buildingMode ? 600 : 900}
                            valueLetterSpacing={buildingMode ? "0.02em" : "normal"}
                            barValue={brand_health?.bars?.named?.value}
                            barFill="#FF2E7E"
                        />
                        <HealthRow
                            testid="dashboard-health-bar-presence"
                            label="Presence"
                            iconBg="#2F6BFF"
                            iconBoxShadow="0 0 18px rgba(47,107,255,0.55)"
                            icon={<HealthIconPresence />}
                            statusText={brand_health?.bars?.presence?.status?.label || "Starting"}
                            statusColor="#8FB0FF"
                            valueText={String(brand_health?.sub_scores?.presence ?? 0)}
                            valueColor="#8FB0FF"
                            barValue={brand_health?.bars?.presence?.value}
                            barFill="#2F6BFF"
                        />
                        <HealthRow
                            testid="dashboard-health-bar-freshness"
                            label="Freshness"
                            iconBg="linear-gradient(135deg, #F6F4EF 0%, #B9B6AD 55%, #ECEBE6 100%)"
                            icon={<HealthIconFreshness />}
                            statusText={brand_health?.bars?.freshness?.status?.label || "Due for new posts"}
                            statusColor="#B9B6AD"
                            valueText={String(brand_health?.sub_scores?.freshness ?? 0)}
                            valueColor="#F6F4EF"
                            barValue={brand_health?.bars?.freshness?.value}
                            barFill="linear-gradient(90deg, #B9B6AD, #F6F4EF)"
                        />
                    </div>
                    {(brand_health?.known_for || []).length > 0 && (
                        <>
                            <div
                                className="pt-4"
                                style={{ borderTop: "1px solid var(--color-line-dark)" }}
                            >
                                <div className="font-mono font-medium text-[11px] tracking-[0.14em] uppercase text-on-ink-muted">
                                    Known for
                                </div>
                            </div>
                            <div
                                data-testid="dashboard-known-for"
                                className="flex flex-wrap gap-2"
                            >
                                {(brand_health?.known_for || []).slice(0, 3).map((t) => (
                                    <Link
                                        key={t.slug}
                                        to={topicHref(t.slug)}
                                        data-testid={`dashboard-known-for-${t.slug}`}
                                        className="inline-flex items-center justify-center min-h-[44px] px-4 rounded-full text-paper font-display text-[14px] font-semibold no-underline"
                                        style={{ background: "var(--color-ink-raised)" }}
                                    >
                                        {t.label} ›
                                    </Link>
                                ))}
                            </div>
                            {!isAdminView && <NotMe dark testid="dashboard-not-me" />}
                        </>
                    )}
                </Tile>

                {/* ----- Tile 1: setup or named rate ----- */}
                {buildingMode ? (
                    <Tile
                        testid="dashboard-tile-setup"
                        eyebrow="SETUP PROGRESS"
                        chip={
                            <Chip kind="signal" testid="dashboard-week-chip">
                                Setting up
                            </Chip>
                        }
                        bottomLeft={
                            <div className="font-mono font-medium text-[11px] tracking-[0.14em] uppercase text-on-ink-muted">
                                First scan
                            </div>
                        }
                        bottomRight={
                            <div
                                data-testid="dashboard-first-check-date"
                                className="font-mono font-medium text-[12px] tracking-[0.1em] uppercase text-paper"
                            >
                                {creator.is_approved ? "RUNNING" : "AFTER YOUR PAGE GOES LIVE"}
                            </div>
                        }
                    >
                        <SetupRing done={doneCount} total={4} />
                        <ul className="space-y-0.5">
                            {checklist.map((c, i) => {
                                const isNext = i === nextIdx;
                                const color = isNext
                                    ? "var(--color-electric-soft)"
                                    : "var(--color-paper)";
                                return (
                                    <li
                                        key={c.label}
                                        data-testid={`dashboard-check-${i}`}
                                        className="flex items-center gap-3 py-2"
                                        style={{ color }}
                                    >
                                        <span
                                            className="inline-flex items-center justify-center w-6 h-6 rounded-full flex-shrink-0"
                                            style={{
                                                background: c.done
                                                    ? "var(--color-signal)"
                                                    : "transparent",
                                                border: c.done || c.running
                                                    ? "none"
                                                    : isNext
                                                    ? "1.5px dashed var(--color-electric)"
                                                    : "1.5px solid var(--color-line-dark)",
                                            }}
                                            aria-hidden
                                        >
                                            {!c.done && c.running && (
                                                <svg
                                                    viewBox="0 0 24 24"
                                                    className="w-6 h-6 animate-spin"
                                                    data-testid={`dashboard-check-${i}-running`}
                                                >
                                                    <circle
                                                        cx="12"
                                                        cy="12"
                                                        r="10"
                                                        fill="none"
                                                        stroke="var(--color-line-dark)"
                                                        strokeWidth="2"
                                                    />
                                                    <path
                                                        d="M12 2a10 10 0 0 1 10 10"
                                                        fill="none"
                                                        stroke="var(--color-electric)"
                                                        strokeWidth="2"
                                                        strokeLinecap="round"
                                                    />
                                                </svg>
                                            )}
                                            {c.done && (
                                                <svg viewBox="0 0 24 24" className="w-3.5 h-3.5">
                                                    <path
                                                        d="M5 12.5l4.5 4.5L19 7"
                                                        fill="none"
                                                        stroke="var(--color-ink)"
                                                        strokeWidth="3"
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                    />
                                                </svg>
                                            )}
                                        </span>
                                        <span className="text-[15px]">{c.label}</span>
                                    </li>
                                );
                            })}
                        </ul>
                    </Tile>
                ) : (
                    <Tile
                        testid="dashboard-tile-named"
                        eyebrow={`NAMED RATE · ${(named.month_label || "").toUpperCase()}`}
                        chip={
                            <Chip
                                kind={
                                    rateBand === "strong"
                                        ? "named"
                                        : rateBand === "growing"
                                        ? "growing"
                                        : "signal"
                                }
                                testid="dashboard-band-chip"
                            >
                                {bandLabel}
                            </Chip>
                        }
                    >
                        <NamedGauge rate={named.named_rate} band={rateBand} />
                        <div
                            data-testid="dashboard-named-line"
                            className="text-center font-display text-paper text-[15px]"
                        >
                            Named in {named.named} of {named.answers} searches
                        </div>
                    </Tile>
                )}

                {/* ----- Newly named banner (active mode only) ----- */}
                {!buildingMode && newlyNamed && (
                    <section
                        data-testid="dashboard-newly-named"
                        className="rounded-[20px] p-5 bg-signal text-ink"
                    >
                        <div className="font-mono font-medium text-[11px] tracking-[0.14em] uppercase">
                            NEW THIS MONTH
                        </div>
                        <div className="font-display font-black text-[22px] mt-1">
                            You're now named for {newlyNamed}.
                        </div>
                    </section>
                )}

                {/* ----- Topics: one row per tracked topic, each opens its page ----- */}
                {topics.length > 0 && (
                    <Tile
                        testid="dashboard-tile-topics"
                        eyebrow="YOUR TOPICS"
                        chip={
                            buildingMode ? (
                                <Chip kind="electric" testid="dashboard-topics-chip">
                                    Tracking
                                </Chip>
                            ) : (
                                <Chip kind="not_yet" testid="dashboard-topics-chip">
                                    {topics.filter((t) => t.state === "named").length} of {topics.length} named
                                </Chip>
                            )
                        }
                        bottomLeft={
                            <div className="font-mono font-medium text-[11px] tracking-[0.14em] uppercase text-on-ink-muted">
                                Tap a topic to see how it's going
                            </div>
                        }
                    >
                        <ul data-testid="dashboard-topics" className="flex flex-col">
                            {topics.map((t, i) => (
                                <li
                                    key={t.slug}
                                    style={
                                        i > 0
                                            ? { borderTop: "1px solid var(--color-line-dark)" }
                                            : undefined
                                    }
                                >
                                    <Link
                                        to={topicHref(t.slug)}
                                        data-testid={`dashboard-topic-${t.slug}`}
                                        className="flex items-center justify-between gap-3 min-h-[52px] py-2 no-underline text-paper"
                                    >
                                        <span className="font-display text-[16px] font-semibold truncate">
                                            {t.label}
                                        </span>
                                        <span className="flex items-center gap-2 shrink-0">
                                            {buildingMode ? (
                                                <Chip kind="not_yet">Tracking</Chip>
                                            ) : (
                                                <Chip kind={t.state || "not_yet"}>
                                                    {t.state === "named"
                                                        ? "Named"
                                                        : t.state === "growing"
                                                        ? "Growing"
                                                        : "Not yet"}
                                                </Chip>
                                            )}
                                            <span aria-hidden="true" className="text-on-ink-muted">
                                                ›
                                            </span>
                                        </span>
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </Tile>
                )}

                {/* ----- Tile 3: working on ----- */}
                <Tile
                    testid="dashboard-tile-working"
                    eyebrow="WHAT WE'RE WORKING ON"
                    bottomLeft={
                        <div className="font-mono font-medium text-[11px] tracking-[0.14em] uppercase text-on-ink-muted">
                            Next check
                        </div>
                    }
                    bottomRight={
                        <div
                            data-testid="dashboard-next-check-date"
                            className="font-mono font-medium text-[12px] tracking-[0.1em] uppercase text-paper"
                        >
                            {fmtDateUpper(placements.next_check_date)}
                        </div>
                    }
                >
                    <div
                        className="rounded-[18px] p-4 flex items-center justify-between gap-3"
                        style={{ background: "var(--color-ink-raised)" }}
                        data-testid="dashboard-working-page"
                    >
                        <div className="min-w-0">
                            <div className="font-display text-paper text-[16px] font-bold">
                                Your page
                            </div>
                            <div className="text-on-ink-muted text-[13px]">
                                Live since {fmtDate(page.approved_at) || "soon"}
                            </div>
                        </div>
                        <Chip kind="signal" testid="dashboard-working-page-chip">
                            Live
                        </Chip>
                    </div>
                    <PlacementNetwork
                        network={placements?.network}
                        items={placements?.items}
                        testid="dashboard-working-placements"
                    />
                    {!buildingMode && next_focus_category && (
                        <div
                            className="rounded-[18px] p-4 flex items-center justify-between gap-3"
                            style={{ background: "var(--color-ink-raised)" }}
                            data-testid="dashboard-working-next-focus"
                        >
                            <div className="min-w-0">
                                <div className="font-display text-paper text-[16px] font-bold">
                                    Next focus
                                </div>
                                <div className="text-on-ink-muted text-[13px]">
                                    {next_focus_category}
                                </div>
                            </div>
                            <Chip kind="electric" testid="dashboard-next-focus-chip">
                                In progress
                            </Chip>
                        </div>
                    )}
                </Tile>

                {!isAdminView && (
                    <Link
                        to="/account"
                        data-testid="dashboard-account-link-footer"
                        className="text-center font-display text-[14px] mt-2 underline underline-offset-4 decoration-signal"
                        style={{ color: "var(--color-ink)" }}
                    >
                        Account and your page
                    </Link>
                )}
            </main>
            {showCheckIn && data?.pending_check_in && (
                <CheckInOverlay
                    pending={data.pending_check_in}
                    creator={data.creator}
                    pageUrl={publicPageLabel(data)}
                    onClose={async () => {
                        setShowCheckIn(false);
                        try {
                            await request("/dashboard/check-in/seen", {
                                method: "POST",
                                body: { run_id: data.pending_check_in.run_id },
                            });
                        } catch {
                            // noop
                        }
                    }}
                />
            )}
        </div>
    );
}

function publicPageLabel(data) {
    if (!data?.creator?.samoony_handle) return "";
    if (data.is_real_domain && data.public_base_url) {
        return `${data.public_base_url.replace(/^https?:\/\/(www\.)?/, "")}/@${data.creator.samoony_handle}`;
    }
    return `samoony.com/@${data.creator.samoony_handle}`;
}

// ---------- Monthly check-in overlay ----------
function CheckInOverlay({ pending, creator, pageUrl, onClose }) {
    const month = pending.month_label || "This month";
    const dateUpper = (() => {
        try {
            return new Date(pending.run_date).toLocaleDateString("en-US", {
                timeZone: EDMONTON,
                month: "short",
                day: "numeric",
            }).toUpperCase();
        } catch {
            return "";
        }
    })();
    const hasNamed = (pending.top_named_topic || "").length > 0;
    const [downloading, setDownloading] = useState(false);

    const renderShareSvg = () => {
        const topic = pending.top_named_topic || pending.top_growing_topic || "";
        const city = creator.city || "";
        const handle = creator.samoony_handle || "";
        const first = (creator.first_name || "").toLowerCase();
        return `
<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 560 996' width='560' height='996'>
  <rect width='560' height='996' fill='#111110' />
  <circle cx='460' cy='120' r='70' fill='#2F6BFF' />
  <circle cx='48' cy='940' r='10' fill='#FF2E7E' />
  <path d='M30 960 A 20 20 0 0 1 70 960' stroke='#FFFFFF' stroke-width='4' fill='none' stroke-linecap='round' />
  <text x='48' y='120' fill='#FFFFFF' font-family='Archivo' font-weight='800' font-size='40'>samoony</text>
  <text x='48' y='340' fill='#8FB0FF' font-family='Geist Mono' font-weight='500' font-size='18' letter-spacing='2.4' text-transform='uppercase'>OFFICIALLY NAMED FOR</text>
  <text x='48' y='410' fill='#FFFFFF' font-family='Archivo' font-weight='900' font-size='52'>${escapeXml(topic || first)}</text>
  <text x='48' y='470' fill='#FFFFFF' font-family='Archivo' font-weight='900' font-size='52'>in <tspan fill='#FF2E7E'>${escapeXml(city || "your city")}.</tspan></text>
  <text x='48' y='560' fill='#B9B6AD' font-family='Archivo' font-weight='500' font-size='26'>@${escapeXml(handle)}</text>
  <rect x='48' y='780' width='464' height='72' rx='36' fill='#FF2E7E' />
  <text x='280' y='827' fill='#111110' font-family='Archivo' font-weight='800' font-size='24' text-anchor='middle'>${escapeXml(pageUrl || "samoony.com")}</text>
</svg>`;
    };

    const svgToPngBlob = async () => {
        const svg = renderShareSvg();
        const svgBlob = new Blob([svg], { type: "image/svg+xml" });
        const url = URL.createObjectURL(svgBlob);
        const img = new Image();
        img.crossOrigin = "anonymous";
        await new Promise((res, rej) => {
            img.onload = res;
            img.onerror = rej;
            img.src = url;
        });
        const canvas = document.createElement("canvas");
        canvas.width = 1080;
        canvas.height = 1920;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, 1080, 1920);
        URL.revokeObjectURL(url);
        return await new Promise((res) => canvas.toBlob(res, "image/png", 0.95));
    };

    const shareOrDownload = async () => {
        if (downloading) return;
        setDownloading(true);
        try {
            const blob = await svgToPngBlob();
            if (blob && navigator.share && navigator.canShare) {
                const file = new File([blob], "samoony-story.png", { type: "image/png" });
                if (navigator.canShare({ files: [file] })) {
                    await navigator.share({ files: [file], title: "I was named." });
                    return;
                }
            }
            downloadBlob(blob, "samoony-story.png");
            toast.success("Image saved");
        } catch {
            toast.error("Could not share. Image was not generated.");
        } finally {
            setDownloading(false);
        }
    };
    const saveOnly = async () => {
        setDownloading(true);
        try {
            const blob = await svgToPngBlob();
            downloadBlob(blob, "samoony-story.png");
            toast.success("Image saved");
        } finally {
            setDownloading(false);
        }
    };

    return (
        <div
            data-testid="check-in-overlay"
            className="fixed inset-0 z-50 overflow-y-auto"
            style={{ background: "var(--color-signal)" }}
        >
            <div className="mx-auto w-full max-w-[480px] px-5 pt-5 pb-16 min-h-screen flex flex-col gap-5">
                <div className="flex items-center justify-between">
                    <div className="font-mono font-medium text-[11px] tracking-[0.14em] uppercase text-ink">
                        {dateUpper} CHECK-IN
                    </div>
                    <button
                        data-testid="check-in-close"
                        onClick={onClose}
                        aria-label="Close"
                        className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-ink text-paper"
                    >
                        <svg viewBox="0 0 24 24" className="w-5 h-5">
                            <path
                                d="M6 6l12 12M18 6L6 18"
                                stroke="var(--color-paper)"
                                strokeWidth="2.5"
                                strokeLinecap="round"
                            />
                        </svg>
                    </button>
                </div>
                <h1
                    data-testid="check-in-headline"
                    className="font-display font-black tracking-tight text-ink text-[52px] leading-[1.0]"
                >
                    Your {month.toLowerCase()} results are in.
                </h1>
                <p
                    data-testid="check-in-sub"
                    className="font-display text-ink text-[16px]"
                >
                    You were named in {pending.named} searches this month, up from {pending.starting_named ?? 0} when you started.
                </p>
                {hasNamed ? (
                    <>
                        <div
                            data-testid="check-in-share-card"
                            className="mx-auto rounded-[20px] overflow-hidden"
                            style={{
                                width: 280,
                                height: 498,
                                background: "#111110",
                                position: "relative",
                            }}
                        >
                            <div
                                aria-hidden
                                className="absolute"
                                style={{
                                    top: 24,
                                    right: 24,
                                    width: 70,
                                    height: 70,
                                    borderRadius: "9999px",
                                    background: "var(--color-electric)",
                                }}
                            />
                            <div
                                className="absolute left-6 top-6 flex items-center gap-2"
                            >
                                <span className="block w-[10px] h-[10px] rounded-full bg-signal" />
                                <svg viewBox="0 0 24 14" className="ml-1 w-[16px] h-[12px]">
                                    <path d="M1 13 A11 11 0 0 1 23 13" stroke="#FFFFFF" strokeWidth="2.5" fill="none" strokeLinecap="round" />
                                </svg>
                                <span className="font-display font-extrabold text-paper text-[14px] ml-1">samoony</span>
                            </div>
                            <div className="absolute left-6 right-6 top-[170px] flex flex-col gap-2">
                                <div className="font-mono font-medium text-[10px] tracking-[0.14em] uppercase" style={{ color: "var(--color-electric-soft)" }}>
                                    OFFICIALLY NAMED FOR
                                </div>
                                <div className="font-display font-black text-paper text-[28px] leading-[1.05] break-words">
                                    {pending.top_named_topic} in <span className="text-signal">{creator.city || "your city"}.</span>
                                </div>
                                <div className="text-on-ink-muted text-[13px] mt-1">
                                    @{creator.samoony_handle}
                                </div>
                            </div>
                            <div className="absolute left-6 right-6 bottom-6">
                                <div className="rounded-full bg-signal text-ink text-center py-3 font-display font-bold text-[14px] break-all">
                                    {pageUrl || "samoony.com"}
                                </div>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <button
                                data-testid="check-in-share"
                                onClick={shareOrDownload}
                                disabled={downloading}
                                className="inline-flex items-center justify-center min-h-[44px] rounded-full px-5 bg-ink text-paper font-display font-bold text-[15px] disabled:opacity-30"
                            >
                                {downloading ? "Preparing..." : "Share to your story"}
                            </button>
                            <button
                                data-testid="check-in-save"
                                onClick={saveOnly}
                                disabled={downloading}
                                className="inline-flex items-center justify-center min-h-[44px] rounded-full px-5 border border-ink text-ink font-display font-bold text-[15px] disabled:opacity-30"
                            >
                                Save image
                            </button>
                        </div>
                    </>
                ) : (
                    <div
                        data-testid="check-in-growing"
                        className="rounded-[20px] bg-ink text-paper p-5 flex flex-col gap-2"
                    >
                        <div className="font-display font-black text-[20px]">
                            {pending.top_growing_topic
                                ? `You're growing in ${pending.top_growing_topic}.`
                                : "You're showing up in more searches."}
                        </div>
                        <div className="text-on-ink-muted text-[14px]">
                            Keep posting about the topics marked Growing. Being named is next.
                        </div>
                    </div>
                )}
                <button
                    data-testid="check-in-see-full"
                    onClick={onClose}
                    className="text-ink text-center font-display text-[14px] underline underline-offset-4 decoration-ink"
                >
                    See full results
                </button>
            </div>
        </div>
    );
}

function escapeXml(s) {
    return String(s || "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
}

function downloadBlob(blob, filename) {
    if (!blob) return;
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
}
